import React from 'react';
import { observer } from 'mobx-react';
import { SpinnerIcon } from '@estee/elc-icons';

import {
    CartItemPanel,
    StyledDetailsWrapper,
    DescriptionWrapper,
    StyledButtonsContainer
} from './CartItemStyles';

import CartRepository from '~repositories/CartRepository/CartRepository';
import { FAVORITES_LIST } from '~setup/constants';
import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';
import { useSpinnerSelector } from '~sharedComponents/SpinnerSelector/SpinnerSelectorHook';

import { StyledImageWrapper } from '~theme/default-theme';
import { classes } from '~utils/ClassNames';

import { CollectionButton } from '~views/Collection/CollectionButton/CollectionButton';
import { ReplenishmentModule } from '~views/ShoppingBagTab/CartSidebarItems/CartItem/ReplenishmentComponent/ReplenishmentComponent';
import { EngravingComponent } from '~views/ShoppingBagTab/CartSidebarItems/CartItem/EngravingComponent/EngravingComponent';

import CartItemPriceWrapper from './CartItemPriceWrapper/CartItemPriceWrapper';
import CartItemDescription from './CartItemDescription/CartItemDescription';
import { ICartItem, ICartItemConfig } from './ICartItem';
import { useRemove } from './RemoveButton/hook/RemoveButtonHook';
import RemoveButton from './RemoveButton/RemoveButton';
import { StyledKitImage } from './CartItemDescription/KitDetails/KitDetailsStyles';
import CartItemAlerts from './CartItemAlerts/CartItemAlerts';

const cartItemName = 'cart-item';
export const removeClass = classes(`${cartItemName}-remove`);
export const removeIconClass = classes(`${cartItemName}-remove-icon`);
export const descriptionWrapperClass = classes(`${cartItemName}-description-wrapper`);
export const detailsWrapperClass = classes('elc-cart-item-details-wrapper');
const buttonsContainerClass = classes(`${cartItemName}-buttons-container`);

const _isDisplayEngraving = (
    hasEngraving: boolean = false,
    engraving: boolean,
    kitInfo: boolean
) => {
    return hasEngraving && !!engraving && !!kitInfo;
};

const CartItem: React.FC<ICartItem> = observer(
    ({ cartItem, config, onRemoveItem, onQuantitySelect }: ICartItem) => {
        const {
            skuId,
            product,
            isReplenishable,
            hasEngraving,
            engraving,
            kitInfo,
            isKitItem,
            enableReplenishment
        } = cartItem;

        const {
            collection: {
                enabled: collectionEnabled = false,
                collectionName = FAVORITES_LIST
            } = {},
            removeTextButton = false,
            removeFreeItems = false,
            productFreeLabel = false,
            showHazmat = false
        } = config as ICartItemConfig;

        const {
            isCartFull: isCartQtyLimitExceeded,
            isCartQtyLimitReached,
            isCartDiscounted
        }: CartRepository = diContainer.get(serviceNames.cartRepository);

        const onQuantitySelected = async (val: number) =>
            onQuantitySelect?.({ skus: [skuId], quantity: val });

        const displayEngraving = _isDisplayEngraving(hasEngraving, !!engraving, !!kitInfo);

        const productUrl = product.isSample ? '' : product.url;
        const showReplenishmentModule = enableReplenishment && isReplenishable;
        const isReadOnly = config?.drawerIsReadOnly || config?.pageIsReadOnly;
        const showAddToCollectionButton = collectionEnabled && !product.isSample;
        const kitThumb = hasEngraving ? product.thumb : kitInfo?.thumb || product.thumb;

        const cartItemPanelClass = classes(
            `${cartItemName}-panel`,
            product.isSample ? 'sample' : ''
        );

        const { showRemove, isRemoving, onKeyPress, onRemovedItem } = useRemove({
            cartItem,
            removeFreeItems,
            onRemoveItem
        });

        const quantitySelectorProps = useSpinnerSelector({
            isCartQtyLimitExceeded,
            isCartQtyLimitReached,
            quantity: cartItem.quantity,
            maxQty: product.maxAllowedQty ?? 6,
            isDropdownDisabled: false,
            onChange: onQuantitySelected
        });

        const { quantityLoading } = quantitySelectorProps;
        const disableActions = isRemoving || quantityLoading;

        return (
            <CartItemPanel data-sku={skuId} role="listitem" className={cartItemPanelClass}>
                {isKitItem ? (
                    <StyledKitImage src={kitThumb} alt="" />
                ) : (
                    <StyledImageWrapper
                        thumb={product.thumb}
                        altThumb={product.altThumb}
                        name={product.name}
                        productUrl={productUrl}
                        alt={product.name}
                    />
                )}
                <StyledDetailsWrapper className={detailsWrapperClass}>
                    <DescriptionWrapper className={descriptionWrapperClass}>
                        <CartItemDescription
                            cartItem={cartItem}
                            config={config as ICartItemConfig}
                            quantitySelectorProps={{ ...quantitySelectorProps, disableActions }}
                            aria-readonly={isReadOnly || true}
                        />
                    </DescriptionWrapper>
                    <CartItemPriceWrapper cartItem={cartItem} />
                    {showReplenishmentModule && <ReplenishmentModule cartItem={cartItem} />}
                    {isRemoving || showRemove || showAddToCollectionButton ? (
                        <StyledButtonsContainer className={buttonsContainerClass}>
                            {isRemoving ? (
                                <SpinnerIcon />
                            ) : (
                                showRemove && (
                                    <RemoveButton
                                        disabled={disableActions}
                                        onClick={onRemovedItem}
                                        onKeyPress={onKeyPress}
                                        productName={product.name}
                                        removeTextButton={removeTextButton}
                                    />
                                )
                            )}
                            {showAddToCollectionButton && (
                                <CollectionButton collectionName={collectionName} sku={skuId} />
                            )}
                        </StyledButtonsContainer>
                    ) : null}
                    <CartItemAlerts
                        cartItem={cartItem}
                        isCartDiscounted={isCartDiscounted}
                        showHazmat={showHazmat}
                    />
                </StyledDetailsWrapper>

                {displayEngraving && (
                    <EngravingComponent
                        engraving={engraving}
                        kitInfo={kitInfo}
                        displayFreeLabel={productFreeLabel}
                    />
                )}
            </CartItemPanel>
        );
    }
);

export default CartItem;
